import React, { useState, useEffect, useCallback } from 'react';
import { inventoryAPI } from '../services/api';
import toast from 'react-hot-toast';

const CATEGORIES = ['engine','brakes','tires','electrical','body','fluids','filters','other'];

const emptyForm = {
  name: '', partNumber: '', category: 'engine', quantity: '',
  minQuantity: '', unitPrice: '', supplier: '', location: '',
};

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.getAll();
      setItems(res.data.data || []);
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, partNumber: item.partNumber || '', category: item.category,
      quantity: item.quantity, minQuantity: item.minQuantity, unitPrice: item.unitPrice,
      supplier: item.supplier || '', location: item.location || '' });
    setShowModal(true);
  };

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) {
        await inventoryAPI.update(editItem._id, form);
        toast.success('Item updated');
      } else {
        await inventoryAPI.create(form);
        toast.success('Item added');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await inventoryAPI.delete(id);
      toast.success('Item deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const handleAdjust = async () => {
    if (!adjustQty) return;
    try {
      await inventoryAPI.adjust(adjustModal._id, { quantity: parseInt(adjustQty), note: adjustNote });
      toast.success('Stock adjusted');
      setAdjustModal(null); setAdjustQty(''); setAdjustNote('');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Adjust failed'); }
  };

  const stockStatus = (item) => {
    if (item.quantity === 0) return 'critical';
    if (item.quantity <= item.minQuantity) return 'low';
    return 'ok';
  };

  const stockPct = (item) => Math.min(100, Math.round((item.quantity / (item.minQuantity * 3 || 30)) * 100));

  const filtered = items.filter(i => {
    const q = search.toLowerCase();
    const matchSearch = !q || i.name.toLowerCase().includes(q) || i.partNumber?.toLowerCase().includes(q);
    const matchCat = catFilter === 'all' || i.category === catFilter;
    const matchStock = stockFilter === 'all'
      || (stockFilter === 'low' && stockStatus(i) === 'low')
      || (stockFilter === 'critical' && stockStatus(i) === 'critical')
      || (stockFilter === 'ok' && stockStatus(i) === 'ok');
    return matchSearch && matchCat && matchStock;
  });

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>Inventory</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{filtered.length} parts & supplies</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Item
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input placeholder="Search parts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 140 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
        </select>
        <select className="form-select" style={{ width: 130 }} value={stockFilter} onChange={e => setStockFilter(e.target.value)}>
          <option value="all">All Stock</option>
          <option value="ok">In Stock</option>
          <option value="low">Low Stock</option>
          <option value="critical">Out of Stock</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <div className="empty-state-title">No items found</div>
          <div className="empty-state-text">Add parts and supplies to track your inventory.</div>
        </div></div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead><tr>
              <th>Part Name</th><th>Part #</th><th>Category</th>
              <th>Stock</th><th>Min Qty</th><th>Unit Price</th><th>Location</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(item => {
                const status = stockStatus(item);
                return (
                  <tr key={item._id}>
                    <td style={{ fontWeight: 600 }}>{item.name}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{item.partNumber || '—'}</td>
                    <td><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{item.category}</span></td>
                    <td>
                      <div style={{ minWidth: 80 }}>
                        <span style={{ fontWeight: 700, color: status === 'critical' ? 'var(--danger)' : status === 'low' ? 'var(--warning)' : 'var(--success)' }}>
                          {item.quantity}
                        </span>
                        <div className="stock-bar">
                          <div className={`stock-bar-fill ${status}`} style={{ width: `${stockPct(item)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td>{item.minQuantity}</td>
                    <td>{fmt(item.unitPrice)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{item.location || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setAdjustModal(item); setAdjustQty(''); setAdjustNote(''); }}>Adjust</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(item)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(item._id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editItem ? 'Edit Item' : 'Add Inventory Item'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Part Name *</label>
                    <input className="form-input" placeholder="e.g. Engine Oil 5W-30" value={form.name} onChange={set('name')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Part Number</label>
                    <input className="form-input" placeholder="e.g. EO-5W30-1L" value={form.partNumber} onChange={set('partNumber')} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-select" value={form.category} onChange={set('category')}>
                      {CATEGORIES.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input className="form-input" placeholder="e.g. Shelf A-3" value={form.location} onChange={set('location')} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity *</label>
                    <input type="number" className="form-input" placeholder="0" value={form.quantity} onChange={set('quantity')} required min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Min Quantity (alert threshold)</label>
                    <input type="number" className="form-input" placeholder="5" value={form.minQuantity} onChange={set('minQuantity')} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Unit Price (₹) *</label>
                    <input type="number" className="form-input" placeholder="0" value={form.unitPrice} onChange={set('unitPrice')} required min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Supplier</label>
                    <input className="form-input" placeholder="Supplier name" value={form.supplier} onChange={set('supplier')} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editItem ? 'Update' : 'Add Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {adjustModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAdjustModal(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <span className="modal-title">Adjust Stock — {adjustModal.name}</span>
              <button className="modal-close" onClick={() => setAdjustModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                Current stock: <strong style={{ color: 'var(--text-primary)' }}>{adjustModal.quantity}</strong>. Enter a positive number to add, negative to deduct.
              </p>
              <div className="form-group">
                <label className="form-label">Quantity Change *</label>
                <input type="number" className="form-input" placeholder="e.g. +10 or -3" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <input className="form-input" placeholder="Reason for adjustment" value={adjustNote} onChange={e => setAdjustNote(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAdjustModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjust} disabled={!adjustQty}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}